import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Trophy, Info, Share2, Users } from 'lucide-react';
import { useEnvAddresses } from '../../hooks/useEnvAddresses';
import { Race, RaceStatus } from '../../types/race';

import firstBadge from '../../assets/first-badge.png';
import secondBadge from '../../assets/second-badge.png';
import thirdBadge from '../../assets/third-badge.png';

import './WatchCard.css';
import RaceTracks from './RaceTracks';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { formatTokenAmount } from '../../utils/formatTokenAmount';
import { formatPrice } from '../../utils/formatPrice';

const BADGES = [firstBadge, secondBadge, thirdBadge];

type WatchCardProps = {
  race: Race;
};

const WatchCard = ({ race }: WatchCardProps) => {
  const { name, totalPool, raceStartTime, tokenStats, handicapEnabled, raceEndTime, totalParticipants, status } = race;
  const navigate = useNavigate();

  const tokensByPriceChange = useMemo(() => {
    return [...tokenStats].sort((a, b) => {
      return b.token.score - a.token.score;
    });
  }, [tokenStats]);

  const [showTooltip, setShowTooltip] = useState(false);
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  const { usdc } = useEnvAddresses();

  const handleWatchRace = () => {
    navigate(`/watch/${race.id}`);
  };

  // Add time calculation logic
  const calculateTimeLeft = useCallback(() => {
    const now = dayjs().unix(); // Current time in seconds
    const timeLeftSeconds = Math.max(0, Number(raceEndTime) - now);

    return {
      hours: Math.floor(timeLeftSeconds / 3600),
      minutes: Math.floor((timeLeftSeconds % 3600) / 60),
      seconds: timeLeftSeconds % 60,
    };
  }, [raceEndTime]);

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  const handleShare = () => {};

  // Update timer every second
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [calculateTimeLeft, raceEndTime]);

  // Helper function to format numbers with leading zeros
  const formatNumber = (num: number) => num.toString().padStart(2, '0');

  return (
    <div className={`race-card watch-card flex flex-col`}>
      <div className="card-header">
        <div className="title-section">
          <div className="title-status">
            <h2>{name}</h2>
          </div>
          <div className="flex gap-3">
            <div className="stats-buttonD action-buttonD" onClick={handleShare} aria-label="Share race">
              <Share2 size={20} />
              {showShareTooltip && <div className="tooltip">Share this race</div>}
            </div>
          </div>
        </div>
      </div>

      <div className="info-section">
        <div className="flex">
          <div className="prize-pool flex-grow">
            <span className="prize-pool-label">Total Staking Reward Pool</span>
            <span className="amount">${formatTokenAmount(totalPool, usdc.decimals, 'usdc')}</span>
          </div>
          <div className="timer-section">
            <div className="timer-label">Time Remaining</div>
            {dayjs.unix(Number(raceEndTime)).isBefore(dayjs()) ? (
              <div className="text-xl font-bold leading-6">Ended</div>
            ) : (
              <div className="horizontal-timer">
                <div className="time-unit">
                  <span className="time-value">{formatNumber(timeLeft.hours)}</span>
                  <span className="time-label">Hrs</span>
                </div>
                <div className="time-unit">
                  <span className="time-value">{formatNumber(timeLeft.minutes)}</span>
                  <span className="time-label">Mins</span>
                </div>
                <div className="time-unit">
                  <span className="time-value">{formatNumber(timeLeft.seconds)}</span>
                  <span className="time-label">Secs</span>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="live-indicator mt-3 !text-sm !font-bold !leading-5">
          <span className="live-dot"></span>
          {dayjs.unix(Number(raceEndTime)).isAfter(dayjs()) ? 'Live Standings' : 'Final Standings'}
        </div>
      </div>
      <div className="divider mt-4"></div>
      <div className="relative flex flex-1 py-6">
        {/* <div className="watch-card-purple-circle"></div> */}
        <div className="flex w-full">
          <div className="relative z-50 flex-none flex-col px-4">
            <div className="font-bold">Ranking</div>
            {tokensByPriceChange.map((tokenStat, index) => {
              return (
                <div key={tokenStat.token.symbol} className={`my-4 flex items-center gap-3`}>
                  {index < 3 ? (
                    <div className="flex h-8 w-8 items-center justify-center overflow-hidden">
                      <img src={BADGES[index]} alt="Badge" width={32} height={32} />
                    </div>
                  ) : (
                    <div className="h-8 w-8"></div>
                  )}
                  <div className="ml-1">
                    <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-white">
                      <img src={tokenStat.token.marketData?.image} alt={`${tokenStat.token.symbol}`} />
                    </div>
                  </div>
                  <div className="flex-grow">
                    <div className="text-sm">{tokenStat.token.symbol}</div>
                    <div className="flex-grow text-xs text-gray-1">{tokenStat.token.name}</div>
                  </div>
                  {
                    <div>
                      <div className="text-sm font-bold">
                        {tokenStat.token.marketData && formatPrice(tokenStat.token.marketData?.current_price)}
                      </div>
                      {status !== RaceStatus.BETTING ? (
                        <div
                          className={`token-change ml-auto text-xs ${
                            tokenStat.token.score >= 0 ? 'positive' : 'negative'
                          }`}
                        >
                          {tokenStat.token.score > 0 ? '+' : ''}
                          {tokenStat.token.score.toFixed(2)}%
                        </div>
                      ) : (
                        <div className="h-5"></div>
                      )}
                    </div>
                  }
                </div>
              );
            })}
          </div>
          <div className="relative flex-1 pl-6">
            <RaceTracks
              tokens={tokensByPriceChange}
              raceStartTime={raceStartTime}
              raceEndTime={raceEndTime}
              trackClassName="right-4 bottom-0 w-2"
              horseImageClassName="w-9 h-9"
              tokenClassName="right-0 top-2"
              tokenIconClassName="w-3 h-3"
              tokenSymbolClassName="text-xs"
              horsePosition={{ first: 'mt-14', rest: 'mt-12' }}
            />
          </div>
        </div>
      </div>
      <div className="divider"></div>
      <div className="mt-auto">
        <div className="mt-4 flex w-full items-center justify-between">
          <div className="race-type">
            <Trophy size={16} />
            <span className="text-sm">Handicap: {handicapEnabled ? 'YES' : 'NO'} </span>
            <div className="relative">
              <Info
                size={16}
                className="info-icon"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              />
              {showTooltip && (
                <div className="tooltip">
                  <div>
                    {`{ Percentage return / 30 day volatility } x
              `}
                  </div>
                  <div>
                    {`
              Ln { ( FDV of token i / min FDV of race participants ) + 1 }`}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Users size={16} className="text-white" />
              <span>{totalParticipants.toString()} Active Participants</span>
            </div>
            {/* <div className="flex -space-x-3">
            {totalParticipants?.slice(0, 3).map((participant, index) => (
              <div key={participant.address} className="avatar" style={{ zIndex: 3 - index }}>
                <img src={participant.avatar} alt={`Participant ${index + 1}`} loading="lazy" />
                <div className="token-badge">
                  <CryptoIconWrapper name={participant.token.toLowerCase()} size={16} />
                </div>
              </div>
            ))}
            {totalParticipants > 3n && <div className="avatar more">+{(totalParticipants - 3n).toString()}</div>}
          </div> */}
          </div>
        </div>

        <button className="watch-button !font-bold" aria-label="Watch Race" onClick={handleWatchRace}>
          Watch Race
        </button>
      </div>
    </div>
  );
};

export default WatchCard;
